import { useState, useRef, useEffect } from "react";
import { SEOHead } from "@/components/SEOHead";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import { Footer } from "@/components/Footer";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Play, Pause, Sparkles, Gift, Star, Phone, PhoneCall } from "lucide-react";
import ellenHeroWebp from "@/assets/ellen-hero.webp";
import ellenHero from "@/assets/ellen-hero.jpg";
import { ChristmasURAGenerator } from "@/components/ChristmasURAGenerator";

const Snowflake = ({ delay = 0, duration = 10 }: { delay?: number; duration?: number }) => (
  <div
    className="absolute text-white/30 animate-float pointer-events-none"
    style={{
      left: `${Math.random() * 100}%`,
      top: `-5%`,
      animationDelay: `${delay}s`,
      animationDuration: `${duration}s`,
      fontSize: `${Math.random() * 10 + 10}px`,
    }}
  >
    ❄
  </div>
);

const christmasItems = [
  {
    name: "Pet Viva - URA Principal Natal",
    category: "URA Completa",
    audio: "/audio/ura-pet-viva-natal.mp3",
  },
  {
    name: "Pet Viva - URA Opções Natal",
    category: "Menu de Opções",
    audio: "/audio/ura-pet-viva-opcoes-natal.mp3",
  },
  {
    name: "Pet Viva - Espera Telefônica Natal",
    category: "Música de Espera",
    audio: "/audio/espera-pet-viva-natal.mp3",
  },
  {
    name: "Monteverde - URA Principal Natal",
    category: "URA Completa",
    audio: "/audio/ura-monteverde-principal.mp3",
  },
  {
    name: "Monteverde - Selecione Opção",
    category: "Menu de Opções",
    audio: "/audio/ura-monteverde-selecione-opcao.mp3",
  },
  {
    name: "Monteverde - Transferência Natal",
    category: "Mensagem de Transferência",
    audio: "/audio/transferencia-monteverde-natal.mp3",
  },
  {
    name: "Monteverde - Espera Telefônica",
    category: "Música de Espera",
    audio: "/audio/espera-monteverde.mp3",
  },
  {
    name: "Monteverde - Day Off Natal",
    category: "Mensagem Fora do Horário",
    audio: "/audio/day-off-monteverde-natal.mp3",
  },
  {
    name: "Clínica Lumier - Boas Festas",
    category: "Mensagem de Boas Festas",
    audio: "/audio/clinica-lumier-boas-festas.mp3",
  },
];

const UrasNatal = () => {
  const [playingIndex, setPlayingIndex] = useState<number | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const handlePlayAudio = (index: number, audioUrl?: string) => {
    if (!audioUrl) return;

    if (playingIndex === index && audioRef.current) {
      audioRef.current.pause();
      setPlayingIndex(null);
      return;
    }

    if (audioRef.current) {
      audioRef.current.pause();
    }

    const audio = new Audio(audioUrl);
    audioRef.current = audio;
    audio.play();
    setPlayingIndex(index);

    audio.onended = () => {
      setPlayingIndex(null);
    };
  };

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
      }
    };
  }, []);

  return (
    <>
      <SEOHead 
        title="URAs de Natal e Ano Novo | Gravação de URA Humanizada"
        description="URAs temáticas de Natal e Ano Novo humanizadas. Surpreenda seus clientes com mensagens festivas profissionais e personalizadas para as festividades."
      />
      <main className="min-h-screen bg-gradient-to-b from-[#0a3d29] via-[#1a1a1a] to-background relative overflow-hidden">
        {/* Snowflakes */}
        <div className="fixed inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
          {Array.from({ length: 30 }).map((_, i) => (
            <Snowflake key={i} delay={i * 0.5} duration={10 + Math.random() * 10} />
          ))}
        </div>

        {/* Christmas lights decoration */}
        <div className="fixed top-0 left-0 right-0 h-8 bg-gradient-to-r from-red-600/20 via-green-600/20 to-red-600/20 border-b-2 border-yellow-400/30" aria-hidden="true">
          <div className="flex justify-around h-full items-end">
            {Array.from({ length: 20 }).map((_, i) => (
              <div
                key={i}
                className="w-3 h-3 rounded-full animate-pulse"
                style={{
                  backgroundColor: i % 3 === 0 ? '#ef4444' : i % 3 === 1 ? '#22c55e' : '#eab308',
                  animationDelay: `${i * 0.2}s`,
                  animationDuration: '2s',
                }}
              />
            ))}
          </div>
        </div>

        {/* Hero Section */}
        <section className="relative min-h-[90vh] flex items-center overflow-hidden pt-16">
          {/* Christmas ornaments */}
          <div className="absolute top-20 left-10 w-16 h-16 bg-red-600/20 rounded-full blur-xl animate-float" aria-hidden="true" />
          <div className="absolute top-40 right-20 w-20 h-20 bg-green-600/20 rounded-full blur-xl animate-float" style={{ animationDelay: '1s' }} aria-hidden="true" />
          <div className="absolute bottom-40 left-1/4 w-12 h-12 bg-yellow-400/20 rounded-full blur-xl animate-float" style={{ animationDelay: '2s' }} aria-hidden="true" />
          
          {/* Decorative stars */}
          <Star className="absolute top-32 right-1/4 w-8 h-8 text-yellow-400/40 animate-pulse" aria-hidden="true" />
          <Star className="absolute bottom-1/3 left-1/3 w-6 h-6 text-yellow-400/30 animate-pulse" style={{ animationDelay: '1s' }} aria-hidden="true" />
          <Gift className="absolute top-1/2 right-12 w-10 h-10 text-red-400/30 animate-float" aria-hidden="true" />
          
          <div className="container mx-auto px-4 py-12 lg:py-20 relative z-10">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              {/* Left Column - Text Content */}
              <div className="space-y-6 lg:space-y-8 text-center lg:text-left">
                {/* Christmas greeting */}
                <div className="text-2xl md:text-3xl font-bold text-yellow-400 animate-pulse mb-4">
                  🎄 Feliz Natal e Próspero Ano Novo! 🎆
                </div>
                
                <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-red-600/20 to-green-600/20 border-2 border-yellow-400/40 backdrop-blur-md mb-4 shadow-lg">
                  <Sparkles className="w-5 h-5 text-yellow-400 animate-pulse" />
                  <span className="text-sm font-semibold text-yellow-400">🎁 Edição Especial de Festas</span>
                  <Sparkles className="w-5 h-5 text-yellow-400 animate-pulse" />
                </div>
                
                <h1 className="text-4xl md:text-6xl font-bold text-white leading-tight drop-shadow-lg">
                  URAs de <span className="text-red-400">Boas</span> <span className="text-yellow-400">Festas</span>
                </h1>
                
                <p className="text-lg md:text-xl text-gray-200 max-w-2xl mx-auto lg:mx-0 drop-shadow-md">
                  Surpreenda seus clientes com URAs temáticas humanizadas para as festividades. 
                  Mensagens profissionais com o verdadeiro espírito natalino! 🎅✨
                </p>

                {/* Mobile Image - Show only on mobile */}
                <div className="lg:hidden flex justify-center mt-6">
                  <div className="relative max-w-xs">
                    {/* Christmas hat decoration */}
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-20 text-6xl animate-float" style={{ animationDelay: '0.5s' }}>
                      🎅
                    </div>
                    <picture>
                      <source srcSet={ellenHeroWebp} type="image/webp" />
                      <img 
                        src={ellenHero} 
                        alt="Ellen, locutora profissional de URA de Natal humanizada" 
                        className="w-full h-auto rounded-3xl shadow-2xl border-4 border-red-200/50"
                        width="448"
                        height="671"
                        {...({ fetchpriority: "high" } as any)}
                      />
                    </picture>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start pt-4">
                  <Button 
                    size="lg" 
                    asChild
                    className="text-base bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white shadow-elegant border-2 border-yellow-400/30"
                  >
                    <a 
                      href="https://api.whatsapp.com/send?phone=5511910456805&text=Olá,%20gostaria%20de%20um%20orçamento%20para%20URA%20de%20Natal"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      🎅 Solicitar URA de Natal
                    </a>
                  </Button>
                </div>
              </div>

              {/* Right Column - Ellen's Image (Desktop Only) */}
              <div className="relative hidden lg:flex lg:h-[700px] items-center justify-center mt-8 lg:mt-0">
                <div className="relative w-full max-w-lg mx-auto">
                  {/* Christmas decorations around the card */}
                  <div className="absolute -top-8 -left-8 text-6xl opacity-40 animate-float" aria-hidden="true">⭐</div>
                  <div className="absolute -top-8 -right-8 text-6xl opacity-40 animate-float" style={{ animationDelay: '1s' }} aria-hidden="true">🎁</div>
                  <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 text-6xl opacity-40 animate-float" style={{ animationDelay: '2s' }} aria-hidden="true">🎄</div>

                  {/* Phone device mockup */}
                  <div className="relative">
                    {/* Christmas hat decoration positioned above Ellen's head */}
                    <div className="absolute -top-8 left-1/2 -translate-x-1/2 z-20 text-8xl animate-float" style={{ animationDelay: '0.5s' }}>
                      🎅
                    </div>

                    {/* Glowing rings */}
                    <div className="absolute inset-0">
                      <div className="absolute inset-0 border-2 border-red-400/20 rounded-[3rem]" />
                      <div className="absolute inset-8 border-2 border-green-400/20 rounded-[2.5rem]" />
                    </div>
                    
                    {/* Main phone */}
                    <div className="relative bg-gradient-to-br from-red-600/30 to-green-600/30 rounded-[3rem] p-4 shadow-phone border-8 border-background">
                      {/* Screen */}
                      <div 
                        className="rounded-[2.5rem] p-8 min-h-[600px] flex flex-col justify-end relative overflow-hidden"
                        role="img"
                        aria-label="Ellen, locutora profissional de URAs de Natal"
                      >
                        <picture className="absolute inset-0">
                          <source srcSet={ellenHeroWebp} type="image/webp" />
                          <img 
                            src={ellenHero} 
                            alt="" 
                            className="absolute inset-0 w-full h-full object-cover rounded-[2.5rem]"
                            width="448"
                            height="671"
                            {...({ fetchpriority: "high" } as any)}
                            aria-hidden="true"
                          />
                        </picture>
                        {/* Christmas overlay gradient */}
                        <div className="absolute inset-0 bg-gradient-to-t from-red-900/50 via-green-900/20 to-transparent" />
                        
                        {/* Call interface - Christmas theme */}
                        <div className="text-center space-y-8 relative z-10 pb-8">
                          <div className="flex gap-4 justify-center">
                            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-red-600 to-red-700 shadow-lg flex items-center justify-center border-2 border-yellow-400/50">
                              <PhoneCall className="w-8 h-8 text-white" />
                            </div>
                            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-green-600 to-green-700 shadow-lg flex items-center justify-center border-2 border-yellow-400/50">
                              <Phone className="w-8 h-8 text-white" />
                            </div>
                          </div>

                          <div className="space-y-2 bg-gradient-to-r from-red-900/90 to-green-900/90 backdrop-blur-md py-4 px-6 rounded-2xl border-2 border-yellow-400/50">
                            <p className="text-white text-2xl font-bold drop-shadow-lg">🎄 Ellen - Locutora</p>
                            <p className="text-yellow-400 text-lg font-semibold drop-shadow-md">URA Especial Final de Ano</p>
                          </div>
                        </div>
                      </div>
                      
                      {/* Home button */}
                      <div className="w-16 h-1.5 bg-white/30 rounded-full mx-auto mt-4" />
                    </div>

                    {/* Floating elements */}
                    <div className="absolute -bottom-6 -right-6 w-24 h-24 bg-red-600/20 rounded-full animate-float backdrop-blur-sm flex items-center justify-center" style={{ animationDelay: '1s' }}>
                      <Gift className="w-12 h-12 text-red-400" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Portfolio Section */}
        <section className="py-20 bg-gradient-to-b from-transparent to-[#0a3d29]/30 relative">
          {/* Decorative elements */}
          <div className="absolute top-10 left-10 text-6xl opacity-20" aria-hidden="true">🎄</div>
          <div className="absolute top-20 right-20 text-5xl opacity-20" aria-hidden="true">🎁</div>
          <div className="absolute bottom-20 left-1/4 text-4xl opacity-20" aria-hidden="true">⭐</div>
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <div className="inline-block mb-4">
                  <span className="text-5xl animate-pulse">🎄</span>
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 drop-shadow-lg">
                  🎅 Exemplos de URAs Temáticas
                </h2>
                <p className="text-xl md:text-2xl text-white font-semibold max-w-3xl mx-auto drop-shadow-lg bg-gradient-to-r from-red-900/60 via-green-900/60 to-red-900/60 backdrop-blur-md py-5 px-8 rounded-2xl border-2 border-yellow-400/40">
                  Ouça exemplos de URAs de Natal e Ano Novo produzidas para nossos clientes 🎶✨
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {christmasItems.map((item, index) => (
                  <Card
                    key={index}
                    className="group cursor-pointer hover:shadow-2xl transition-all duration-300 overflow-hidden bg-gradient-to-br from-white/95 to-white/90 backdrop-blur-sm border-2 border-red-200/50 hover:border-yellow-400/60 hover:scale-105"
                    onClick={() => handlePlayAudio(index, item.audio)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handlePlayAudio(index, item.audio);
                      }
                    }}
                    tabIndex={0}
                    role="button"
                    aria-label={`Reproduzir ${item.name}`}
                  >
                    <div className="p-6 relative">
                      {/* Christmas decoration corner */}
                      <div className="absolute top-2 right-2 text-2xl opacity-40" aria-hidden="true">
                        {index % 3 === 0 ? '🎄' : index % 3 === 1 ? '🎁' : '⭐'}
                      </div>
                      
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <h3 className="font-semibold text-foreground mb-2 group-hover:text-red-600 transition-colors">
                            {item.name}
                          </h3>
                          <span className="text-sm text-muted-foreground inline-flex items-center gap-1">
                            🎵 {item.category}
                          </span>
                        </div>
                        <div className="flex-shrink-0 ml-4">
                          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-red-500/20 to-green-500/20 flex items-center justify-center group-hover:from-red-500/30 group-hover:to-green-500/30 transition-all border-2 border-yellow-400/30">
                            {playingIndex === index ? (
                              <Pause className="w-6 h-6 text-red-600" />
                            ) : (
                              <Play className="w-6 h-6 text-green-700" />
                            )}
                          </div>
                        </div>
                      </div>
                      {playingIndex === index && (
                        <div className="mt-4 pt-4 border-t border-red-200">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-2 bg-gradient-to-r from-red-200 to-green-200 rounded-full overflow-hidden">
                              <div className="h-full bg-gradient-to-r from-red-600 to-green-600 animate-pulse rounded-full" style={{ width: "100%" }} />
                            </div>
                            <span className="text-xs text-red-600 font-semibold">Tocando 🎶</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Music Tracks Section */}
        <section className="py-20 bg-gradient-to-b from-[#0a3d29]/30 to-transparent relative">
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <div className="inline-block mb-4">
                  <span className="text-5xl animate-pulse">🎵</span>
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 drop-shadow-lg">
                  🎄 Trilhas Musicais Natalinas
                </h2>
                <p className="text-lg text-gray-200 max-w-2xl mx-auto drop-shadow-md">
                  Escolha a trilha musical perfeita para sua URA de Natal 🎶✨
                </p>
              </div>

              <div className="max-w-4xl mx-auto bg-gradient-to-br from-white/95 to-white/90 backdrop-blur-sm border-2 border-green-200/50 rounded-lg shadow-elegant overflow-hidden">
                <div className="max-h-[600px] overflow-y-auto scrollbar-thin scrollbar-thumb-green-600 scrollbar-track-green-100/50">
                  <div className="divide-y divide-green-100">
                    {[
                      { name: "Trilha 1", audio: "/audio/christmas-village.mp3" },
                      { name: "Trilha 2", audio: "/audio/dead-forest.mp3" },
                      { name: "Trilha 3", audio: "/audio/deck-the-halls.mp3" },
                      { name: "Trilha 4", audio: "/audio/deck-the-halls-b.mp3" },
                      { name: "Trilha 5", audio: "/audio/hark-the-herald.mp3" },
                      { name: "Trilha 6", audio: "/audio/holly-dazed.mp3" },
                      { name: "Trilha 7", audio: "/audio/i-saw-three-ships.mp3" },
                      { name: "Trilha 8", audio: "/audio/bethlehem-christmas.mp3" },
                      { name: "Trilha 9", audio: "/audio/canon-and-variation.mp3" },
                      { name: "Trilha 10", audio: "/audio/christmas-chores.mp3" },
                      { name: "Trilha 11", audio: "/audio/jingle-bells-jazz.mp3" },
                      { name: "Trilha 12", audio: "/audio/jingle-bells-7.mp3" },
                      { name: "Trilha 13", audio: "/audio/jingle-bells-calm.mp3" },
                      { name: "Trilha 14", audio: "/audio/jolly-old-st-nicholas.mp3" },
                      { name: "Trilha 15", audio: "/audio/joy-to-the-world.mp3" },
                      { name: "Trilha 16", audio: "/audio/oh-little-town-bethlehem.mp3" },
                      { name: "Trilha 17", audio: "/audio/angels-we-have-heard.mp3" },
                      { name: "Trilha 18", audio: "/audio/it-came-upon-midnight-clear.mp3" },
                      { name: "Trilha 19", audio: "/audio/jesu-joy-of-mans-desiring.mp3" },
                      { name: "Trilha 20", audio: "/audio/jingle-bells.mp3" },
                    ].map((track, index) => (
                      <div
                        key={index}
                        className="group cursor-pointer hover:bg-green-50/50 transition-all duration-300 p-4"
                        onClick={() => handlePlayAudio(christmasItems.length + index, track.audio)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            handlePlayAudio(christmasItems.length + index, track.audio);
                          }
                        }}
                        tabIndex={0}
                        role="button"
                        aria-label={`Reproduzir ${track.name}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4 flex-1">
                            <div className="flex-shrink-0">
                              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-green-500/20 to-red-500/20 flex items-center justify-center group-hover:from-green-500/30 group-hover:to-red-500/30 transition-all border-2 border-yellow-400/30">
                                {playingIndex === (christmasItems.length + index) ? (
                                  <Pause className="w-6 h-6 text-red-600" />
                                ) : (
                                  <Play className="w-6 h-6 text-green-700" />
                                )}
                              </div>
                            </div>
                            <div className="flex-1 min-w-0">
                              <h3 className="font-semibold text-foreground mb-1 group-hover:text-green-700 transition-colors truncate">
                                {track.name}
                              </h3>
                            </div>
                          </div>
                          <div className="flex-shrink-0 ml-4">
                            <span className="text-2xl opacity-40">🎵</span>
                          </div>
                        </div>
                        
                        {playingIndex === (christmasItems.length + index) && (
                          <div className="mt-3 pt-3 border-t border-green-200">
                            <div className="flex items-center gap-2">
                              <div className="flex-1 h-2 bg-gradient-to-r from-green-200 to-red-200 rounded-full overflow-hidden">
                                <div className="h-full bg-gradient-to-r from-green-600 to-red-600 animate-pulse rounded-full" style={{ width: "100%" }} />
                              </div>
                              <span className="text-xs text-green-600 font-semibold whitespace-nowrap">Tocando 🎶</span>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Christmas URA Generator Section */}
        <ChristmasURAGenerator />

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-br from-red-900/20 via-green-900/20 to-transparent relative overflow-hidden">
          {/* Decorative background */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-10 left-10 text-8xl">🎄</div>
            <div className="absolute top-1/2 right-10 text-8xl">🎅</div>
            <div className="absolute bottom-10 left-1/3 text-8xl">🎁</div>
          </div>
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-3xl mx-auto text-center space-y-6 bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-md border-2 border-yellow-400/30 rounded-3xl p-8 md:p-12 shadow-2xl">
              <div className="text-5xl mb-4 animate-pulse">🎅🎄🎁</div>
              <h2 className="text-3xl md:text-4xl font-bold text-white drop-shadow-lg">
                Pronto para criar sua URA de Natal?
              </h2>
              <p className="text-lg text-gray-200 drop-shadow-md">
                Entre em contato agora e receba um orçamento personalizado para suas URAs temáticas! 
                Garanta suas gravações antes das festas! 🎊
              </p>
              <div className="pt-4">
                <Button 
                  size="lg" 
                  asChild
                  className="text-base bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white shadow-elegant border-2 border-yellow-400/50 hover:scale-105 transition-transform"
                >
                  <a 
                    href="https://api.whatsapp.com/send?phone=5511910456805&text=Olá,%20gostaria%20de%20um%20orçamento%20para%20URA%20de%20Natal"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    🎄 Solicitar Orçamento Agora
                  </a>
                </Button>
              </div>
              <p className="text-sm text-yellow-400 font-semibold animate-pulse">
                ⏰ Prazo limitado para entrega antes das festas!
              </p>
            </div>
          </div>
        </section>

        <Footer />
      </main>
      <WhatsAppButton />
    </>
  );
};

export default UrasNatal;
